import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { COLORS, FONTS } from '../../constants/config';
import { useAuth } from '../../contexts/AuthContext';
import OrgChartFlow from './OrgChartFlow';
import OrgChartColorSettings from './OrgChartColorSettings';
import type { TreeNode } from './orgChartUtils';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
export interface OrganizationalChartProps {
  onNodePress?: (node: TreeNode) => void;
  onDirectoratePress?: (node: TreeNode) => void;
  onDepartmentPress?: (node: TreeNode) => void;
  onMoveComplete?: () => void;
  organizationName?: string;
  organizationId?: number;
}

// Static CSS for drop target highlighting — injected once via useEffect
const DROP_TARGET_CSS = `
  .org-drop-child > div {
    outline: 3px solid #22C55E !important;
    outline-offset: 2px;
    border-radius: 10px;
    box-shadow: 0 0 16px rgba(34, 197, 94, 0.4) !important;
  }
  .org-drop-sibling > div {
    outline: 3px solid #3B82F6 !important;
    outline-offset: 2px;
    border-radius: 10px;
    box-shadow: 0 0 16px rgba(59, 130, 246, 0.4) !important;
  }
`;

export default function OrganizationalChart({
  onNodePress,
  onMoveComplete,
  organizationName,
  organizationId,
}: OrganizationalChartProps) {
  const { user } = useAuth();
  const resolvedOrgId = organizationId || user?.profile?.organization;
  const canEdit = user?.profile?.user_type === 'org_employee';
  const [showColors, setShowColors] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [moveCount, setMoveCount] = useState(0);
  const [isUndoing, setIsUndoing] = useState(false);
  const confirmRef = useRef<(() => void) | null>(null);
  const undoRef = useRef<(() => Promise<void>) | null>(null);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    setShowColors(false);
    setMoveCount(0);
  }, []);

  const handleSave = useCallback(() => {
    if (confirmRef.current) {
      confirmRef.current();
    }
    setIsEditing(false);
    setMoveCount(0);
  }, []);

  const handleCancel = useCallback(async () => {
    if (undoRef.current && moveCount > 0) {
      setIsUndoing(true);
      await undoRef.current();
      setIsUndoing(false);
    }
    setIsEditing(false);
    setMoveCount(0);
  }, [moveCount]);

  // Inject CSS once
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const style = document.createElement('style');
    style.textContent = DROP_TARGET_CSS;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const handleColorsSaved = useCallback(() => {
    setShowColors(false);
    setRefreshKey((k) => k + 1);
  }, []);

  if (Platform.OS !== 'web') {
    return null;
  }

  return (
    <View style={styles.container}>
      {/* Toolbar */}
      <View style={styles.toolbar}>
        {isEditing ? (
          <View style={styles.editToolbar}>
            <Button
              variant="secondary"
              onPress={handleCancel}
              loading={isUndoing}
              disabled={isUndoing}
            >
              {moveCount > 0 ? `تراجع (${moveCount})` : 'إلغاء'}
            </Button>
            <Button
              variant="primary"
              onPress={handleSave}
              disabled={isUndoing}
            >
              تم
            </Button>
            <Text style={styles.editHint}>اسحب الوحدات لإعادة ترتيبها</Text>
          </View>
        ) : (
          <View style={styles.editToolbar}>
            <TouchableOpacity
              style={[styles.colorBtn, showColors && styles.colorBtnActive]}
              onPress={() => setShowColors((v) => !v)}
              activeOpacity={0.7}
            >
              <Text style={styles.colorBtnEmoji}>🎨</Text>
              <Text style={styles.colorBtnText}>ألوان الهيكل</Text>
            </TouchableOpacity>
            {canEdit && (
              <Button variant="primary" onPress={handleEdit}>
                تعديل الهيكل
              </Button>
            )}
          </View>
        )}
      </View>

      {/* Color settings panel — rendered BEFORE chart so it layers on top */}
      {showColors && resolvedOrgId && (
        <View style={styles.colorPanelWrapper} pointerEvents="box-none">
          <OrgChartColorSettings
            organizationId={resolvedOrgId}
            onSave={handleColorsSaved}
            onClose={() => setShowColors(false)}
          />
        </View>
      )}

      {/* React Flow container */}
      <View style={[styles.chartContainer, showColors && { zIndex: 1 }]}>
        <OrgChartFlow
          key={refreshKey}
          organizationId={resolvedOrgId}
          organizationName={organizationName}
          onNodePress={onNodePress}
          onMoveComplete={onMoveComplete}
          isEditing={isEditing}
          onMoveCountChange={setMoveCount}
          confirmRef={confirmRef}
          undoRef={undoRef}
        />
      </View>
    </View>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    position: 'relative',
  },
  toolbar: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    backgroundColor: '#FAFAFA',
    borderTopStartRadius: 12,
    borderTopEndRadius: 12,
    borderWidth: tokens.spacing.absolute["px"],
    borderBottomWidth: 0,
    borderColor: tokens.colors.raw.gray["200"],
  },
  editToolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    flex: 1,
    justifyContent: 'flex-end',
  },
  editHint: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
    marginEnd: 'auto' as any,
  },
  colorBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    paddingVertical: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    backgroundColor: tokens.colors.raw.white,
  },
  colorBtnActive: {
    backgroundColor: '#EFF6FF',
    borderColor: tokens.colors.raw.blue["500"],
  },
  colorBtnEmoji: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
  },
  colorBtnText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["700"],
  },
  colorPanelWrapper: {
    position: 'absolute',
    top: 44,
    end: 12,
    zIndex: 9999,
    elevation: 9999,
  },
  chartContainer: {
    width: '100%',
    height: 560,
    overflow: 'hidden',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderBottomStartRadius: 12,
    borderBottomEndRadius: 12,
    backgroundColor: '#FAFAFA',
  },
});

const styles = getStyles(true);
